import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Search, Filter, FileText, FileCheck, MessageSquare, 
  CheckCircle2, AlertCircle, Clock, Eye, Send, ChevronDown, ChevronUp,
  Download, User, Calendar, Shield, HelpCircle, Image as ImageIcon, ExternalLink, BookOpen
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  getScheduledAuditById,
  getAuditControls,
  getControlDetail,
  updateControlReview,
  addComment,
  getAuditSummary,
  REVIEW_STATUS,
  COMMENT_TYPES,
  getReviewStatusBadge
} from '@/services/scheduledAudit';
import { toast } from 'sonner';

const AuditorReviewPage = () => {
  const { auditId } = useParams();
  const navigate = useNavigate();
  
  const [audit, setAudit] = useState(null);
  const [summary, setSummary] = useState(null);
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [domainFilter, setDomainFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [domains, setDomains] = useState([]);
  
  // Control Detail Panel
  const [selectedControl, setSelectedControl] = useState(null);
  const [controlDetail, setControlDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Comment Dialog
  const [showCommentDialog, setShowCommentDialog] = useState(false);
  const [commentData, setCommentData] = useState({
    comment_type: 'comment',
    content: ''
  });
  
  // Expanded sections in control detail
  const [expandedSections, setExpandedSections] = useState({
    documents: true,
    evidence: true,
    policies: true,
    comments: true
  });

  // Evidence & policy preview modals (auditors can inspect screenshots / policy content)
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPayload, setPreviewPayload] = useState(null);   // { kind, title, ... }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [policyPreview, setPolicyPreview] = useState(null);
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const authHeaders = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };
  const isImageEvidence = (ev) => {
    const t = (ev?.file_type || '').toLowerCase();
    return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(t);
  };
  const openEvidencePreview = async (ev) => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewPayload({ kind: 'loading', title: ev.title });
    try {
      const { data } = await axios.get(`${API_BASE}/api/grc/evidence/${ev.id}/preview`, { headers: authHeaders() });
      setPreviewPayload({ ...data, evidence: ev });
    } catch (err) {
      setPreviewPayload({ kind: 'error', title: ev.title, error: err?.response?.data?.detail || 'Preview unavailable' });
    } finally {
      setPreviewLoading(false);
    }
  };
  const openDocumentPreview = async (doc) => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewPayload({ kind: 'loading', title: doc.title });
    try {
      const { data } = await axios.get(`${API_BASE}/api/grc/documents/${doc.id}/preview`, { headers: authHeaders() });
      setPreviewPayload({ ...data, document: doc, source: 'document' });
    } catch (err) {
      setPreviewPayload({ kind: 'error', title: doc.title, error: err?.response?.data?.detail || 'Preview unavailable' });
    } finally {
      setPreviewLoading(false);
    }
  };
  const openPolicyPreview = async (policy) => {
    try {
      const { data } = await axios.get(`${API_BASE}/api/grc/policies/${policy.id}`, { headers: authHeaders() });
      setPolicyPreview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load policy');
    }
  };

  useEffect(() => {
    loadAuditData();
  }, [auditId]);

  useEffect(() => {
    if (audit) {
      loadControls();
    }
  }, [audit, search, domainFilter, statusFilter]);

  const loadAuditData = async () => {
    try {
      setLoading(true);
      const [auditData, summaryData] = await Promise.all([
        getScheduledAuditById(auditId),
        getAuditSummary(auditId)
      ]);
      setAudit(auditData);
      setSummary(summaryData);
      
      // Extract unique domains
      if (summaryData?.domain_breakdown) {
        setDomains(Object.keys(summaryData.domain_breakdown));
      }
    } catch (error) {
      toast.error('Failed to load audit data');
    } finally {
      setLoading(false);
    }
  };

  const loadControls = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (domainFilter !== 'all') params.domain = domainFilter;
      if (statusFilter !== 'all') params.review_status = statusFilter;
      
      const data = await getAuditControls(auditId, params);
      setControls(data.controls || []);
    } catch (error) {
      toast.error('Failed to load controls');
    }
  };

  const loadControlDetail = async (control) => {
    try {
      setLoadingDetail(true);
      setSelectedControl(control);
      const data = await getControlDetail(auditId, control.id);
      setControlDetail(data);
    } catch (error) {
      toast.error('Failed to load control details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleUpdateReviewStatus = async (status) => {
    if (!selectedControl) return;
    
    try {
      await updateControlReview(auditId, selectedControl.id, { status });
      toast.success('Review status updated');
      loadControls();
      loadControlDetail(selectedControl);
      loadAuditData();
    } catch (error) {
      toast.error('Failed to update review status');
    }
  };

  const handleAddComment = async () => {
    if (!commentData.content.trim()) {
      toast.error('Please enter a comment');
      return;
    }
    
    try {
      await addComment(auditId, selectedControl.id, commentData);
      toast.success('Comment added');
      setShowCommentDialog(false);
      setCommentData({ comment_type: 'comment', content: '' });
      loadControlDetail(selectedControl);
      
      // If clarification request, reload controls to update status
      if (commentData.comment_type === 'clarification_request') {
        loadControls();
      }
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleCompleteAssessment = async () => {
    if (!window.confirm('Mark this assessment as complete? This will finalize the audit and lock further reviews.')) return;
    try {
      const { data } = await axios.post(
        `${API_BASE}/api/scheduled-audits/${auditId}/complete`,
        {},
        { headers: authHeaders() }
      );
      toast.success(`Assessment completed by ${data.completed_by_name || 'you'} — downloading report…`);
      loadAuditData();
      // Trigger the completion PDF download immediately.
      downloadCompletionReport();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to complete assessment');
    }
  };

  const downloadCompletionReport = async () => {
    try {
      const resp = await axios.get(
        `${API_BASE}/api/scheduled-audits/${auditId}/report.pdf`,
        { headers: authHeaders(), responseType: 'blob' }
      );
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${audit?.audit_id || 'assessment'}-completion-report.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not download the completion report');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'reviewed':
      case 'approved':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'issues_found':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      case 'needs_clarification':
        return <HelpCircle className="h-4 w-4 text-orange-600" />;
      case 'in_review':
        return <Clock className="h-4 w-4 text-blue-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading audit...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Left Panel - Controls List */}
      <div className="w-1/2 border-r bg-white overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b bg-white">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/grc/audit/schedule')}
            className="mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Scheduled Audits
          </Button>
          
          <h1 className="text-xl font-bold text-gray-900">{audit?.title}</h1>
          <p className="text-sm text-gray-600">{audit?.framework_name}</p>

          {/* Complete Assessment CTA — visible only when every control has been reviewed */}
          {summary && audit?.status !== 'completed' && summary.progress.total_controls > 0 && summary.progress.reviewed_controls >= summary.progress.total_controls && (
            <Button
              variant="default"
              size="sm"
              className="mt-3 bg-emerald-600 hover:bg-emerald-700"
              onClick={handleCompleteAssessment}
              data-testid="complete-assessment-btn"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Complete Assessment ({summary.progress.reviewed_controls}/{summary.progress.total_controls})
            </Button>
          )}
          {/* Completion metadata banner */}
          {audit?.status === 'completed' && audit?.completed_at && (
            <div className="mt-3 p-2 rounded-md bg-emerald-50 border border-emerald-200 text-xs text-emerald-800" data-testid="assessment-completion-banner">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2 font-semibold">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Assessment completed
                  </div>
                  <div className="mt-1">
                    {audit.completed_by_name || audit.completed_by_email || 'Auditor'} · {formatDate(audit.completed_at)}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7"
                  onClick={downloadCompletionReport}
                  data-testid="download-completion-report-btn"
                >
                  <Download className="h-3.5 w-3.5 mr-1" />
                  Download Report
                </Button>
              </div>
            </div>
          )}
          
          {/* Progress Summary */}
          {summary && (
            <div className="mt-4 grid grid-cols-4 gap-2 text-center">
              <div className="bg-gray-50 rounded p-2">
                <p className="text-lg font-bold">{summary.progress.total_controls}</p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
              <div className="bg-green-50 rounded p-2">
                <p className="text-lg font-bold text-green-600">{summary.progress.reviewed_controls}</p>
                <p className="text-xs text-gray-500">Reviewed</p>
              </div>
              <div className="bg-red-50 rounded p-2">
                <p className="text-lg font-bold text-red-600">{summary.progress.controls_with_issues}</p>
                <p className="text-xs text-gray-500">Issues</p>
              </div>
              <div className="bg-blue-50 rounded p-2">
                <p className="text-lg font-bold text-blue-600">{summary.progress.completion_percentage}%</p>
                <p className="text-xs text-gray-500">Complete</p>
              </div>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="p-4 border-b space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search controls..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            <Select value={domainFilter} onValueChange={setDomainFilter}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Domain" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Domains</SelectItem>
                {domains.map(domain => (
                  <SelectItem key={domain} value={domain}>{domain}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {REVIEW_STATUS.map(status => (
                  <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Controls List */}
        <div className="flex-1 overflow-y-auto">
          {controls.length === 0 ? (
            <div className="p-8 text-center">
              <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No controls found</p>
            </div>
          ) : (
            controls.map(control => {
              const statusBadge = getReviewStatusBadge(control.review_status);
              const isSelected = selectedControl?.id === control.id;
              
              return (
                <div
                  key={control.id}
                  className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                    isSelected ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''
                  }`}
                  onClick={() => loadControlDetail(control)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(control.review_status)}
                        <span className="font-medium text-sm truncate">{control.control_id}</span>
                      </div>
                      <p className="text-sm text-gray-700 mt-1 line-clamp-2">{control.control_name}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-xs">{control.domain}</Badge>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge.color}`}>
                          {statusBadge.label}
                        </span>
                      </div>
                    </div>
                    <div className="text-right ml-2">
                      {control.unresolved_clarifications > 0 && (
                        <Badge variant="destructive" className="text-xs">
                          {control.unresolved_clarifications} pending
                        </Badge>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {control.documents?.length || 0} docs, {control.evidence?.length || 0} evidence
                      </p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Right Panel - Control Detail */}
      <div className="w-1/2 overflow-hidden flex flex-col bg-white">
        {!selectedControl ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Eye className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Select a control to review</p>
            </div>
          </div>
        ) : loadingDetail ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : controlDetail ? (
          <>
            {/* Control Header */}
            <div className="p-4 border-b bg-gray-50">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-bold">{controlDetail.control.control_id}</h2>
                  <p className="text-sm text-gray-600 mt-1">{controlDetail.control.control_name}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowCommentDialog(true)}
                  >
                    <MessageSquare className="h-4 w-4 mr-1" />
                    Comment
                  </Button>
                </div>
              </div>
              
              {/* Review Status Actions */}
              <div className="mt-4 flex items-center gap-2">
                <span className="text-sm text-gray-600">Mark as:</span>
                {['reviewed', 'needs_clarification', 'issues_found', 'approved'].map(status => {
                  const badge = getReviewStatusBadge(status);
                  const isActive = controlDetail.review.status === status;
                  return (
                    <Button
                      key={status}
                      size="sm"
                      variant={isActive ? 'default' : 'outline'}
                      className={isActive ? '' : 'hover:bg-gray-100'}
                      onClick={() => handleUpdateReviewStatus(status)}
                    >
                      {badge.label}
                    </Button>
                  );
                })}
              </div>
            </div>

            {/* Control Details Scrollable Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Requirement Text */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Requirement</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-700">{controlDetail.control.requirement_text}</p>
                  {controlDetail.control.evidence_requirements && (
                    <div className="mt-3 p-2 bg-blue-50 rounded text-sm">
                      <strong>Evidence Requirements:</strong> {controlDetail.control.evidence_requirements}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Ownership Info */}
              <Card>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Owner</p>
                      <p className="font-medium">{controlDetail.control.owner || '-'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Reviewer</p>
                      <p className="font-medium">{controlDetail.control.reviewer || '-'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Approver</p>
                      <p className="font-medium">{controlDetail.control.approver || '-'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Documents Section */}
              <Card>
                <CardHeader 
                  className="pb-2 cursor-pointer flex flex-row items-center justify-between"
                  onClick={() => toggleSection('documents')}
                >
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Documents ({controlDetail.documents?.length || 0})
                  </CardTitle>
                  {expandedSections.documents ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </CardHeader>
                {expandedSections.documents && (
                  <CardContent>
                    {controlDetail.documents?.length === 0 ? (
                      <p className="text-sm text-gray-500">No documents linked</p>
                    ) : (
                      <div className="space-y-2">
                        {controlDetail.documents?.map(doc => (
                          <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-50 rounded" data-testid={`audit-document-row-${doc.id}`}>
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText className="h-4 w-4 text-blue-600 shrink-0" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{doc.title}</p>
                                <p className="text-xs text-gray-500 truncate">
                                  {doc.document_type ? `${doc.document_type} · ` : ''}
                                  {doc.owner || 'Unassigned'}
                                  {doc.file_type ? ` · ${(doc.file_type || '').toUpperCase()}` : ''}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2"
                                onClick={() => openDocumentPreview(doc)}
                                data-testid={`document-preview-btn-${doc.id}`}
                              >
                                <Eye className="h-3.5 w-3.5 mr-1" />View
                              </Button>
                              <a
                                href={`${API_BASE}/api/grc/documents/${doc.id}/download`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center h-7 px-2 rounded-md border text-xs hover:bg-white"
                                data-testid={`document-download-btn-${doc.id}`}
                              >
                                <Download className="h-3.5 w-3.5 mr-1" />Download
                              </a>
                              <Badge variant={doc.approval_status === 'approved' ? 'default' : 'secondary'}>
                                {doc.approval_status || 'pending'}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>

              {/* Evidence Section */}
              <Card>
                <CardHeader 
                  className="pb-2 cursor-pointer flex flex-row items-center justify-between"
                  onClick={() => toggleSection('evidence')}
                >
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <FileCheck className="h-4 w-4" />
                    Evidence ({controlDetail.evidence?.length || 0})
                  </CardTitle>
                  {expandedSections.evidence ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </CardHeader>
                {expandedSections.evidence && (
                  <CardContent>
                    {controlDetail.evidence?.length === 0 ? (
                      <p className="text-sm text-gray-500">No evidence uploaded</p>
                    ) : (
                      <div className="space-y-2">
                        {controlDetail.evidence?.map(ev => (
                          <div key={ev.id} className="flex items-center justify-between p-2 bg-gray-50 rounded" data-testid={`audit-evidence-row-${ev.id}`}>
                            <div className="flex items-center gap-2 min-w-0">
                              {isImageEvidence(ev) && ev.file_path ? (
                                <img
                                  src={`${API_BASE}/api/grc/evidence/${ev.id}/inline`}
                                  alt={ev.title}
                                  className="h-10 w-10 object-cover rounded border cursor-zoom-in"
                                  onClick={() => openEvidencePreview(ev)}
                                  data-testid={`evidence-thumb-${ev.id}`}
                                />
                              ) : isImageEvidence(ev) ? (
                                <ImageIcon className="h-4 w-4 text-green-600" />
                              ) : (
                                <FileCheck className="h-4 w-4 text-green-600" />
                              )}
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{ev.title}</p>
                                <p className="text-xs text-gray-500 truncate">
                                  {ev.file_type ? `${(ev.file_type || '').toUpperCase()} · ` : ''}
                                  {ev.review_frequency || 'ad-hoc'}
                                  {ev.next_review_date ? ` · Next review: ${formatDate(ev.next_review_date)}` : ''}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2"
                                onClick={() => openEvidencePreview(ev)}
                                data-testid={`evidence-preview-btn-${ev.id}`}
                              >
                                <Eye className="h-3.5 w-3.5 mr-1" />View
                              </Button>
                              <Badge variant={ev.approval_status === 'approved' ? 'default' : 'secondary'}>
                                {ev.approval_status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>

              {/* Policies Section — auditors can open the full policy text */}
              <Card>
                <CardHeader
                  className="pb-2 cursor-pointer flex flex-row items-center justify-between"
                  onClick={() => toggleSection('policies')}
                >
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <BookOpen className="h-4 w-4" />
                    Policies ({controlDetail.policies?.length || 0})
                  </CardTitle>
                  {expandedSections.policies ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </CardHeader>
                {expandedSections.policies && (
                  <CardContent>
                    {controlDetail.policies?.length === 0 ? (
                      <p className="text-sm text-gray-500">No policies linked to this control</p>
                    ) : (
                      <div className="space-y-2">
                        {controlDetail.policies?.map(policy => (
                          <div key={policy.id} className="flex items-center justify-between p-2 bg-gray-50 rounded" data-testid={`audit-policy-row-${policy.id}`}>
                            <div className="flex items-center gap-2 min-w-0">
                              <BookOpen className="h-4 w-4 text-indigo-600 shrink-0" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{policy.title}</p>
                                <p className="text-xs text-gray-500 truncate">
                                  {policy.category ? `${policy.category} · ` : ''}
                                  v{policy.version || 1} · {policy.owner || 'Unassigned'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2"
                                onClick={() => openPolicyPreview(policy)}
                                data-testid={`policy-preview-btn-${policy.id}`}
                              >
                                <Eye className="h-3.5 w-3.5 mr-1" />View
                              </Button>
                              <a
                                href={`${API_BASE}/api/grc/policies/${policy.id}/export/pdf`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center h-7 px-2 rounded-md border text-xs hover:bg-white"
                                data-testid={`policy-pdf-btn-${policy.id}`}
                              >
                                <Download className="h-3.5 w-3.5 mr-1" />PDF
                              </a>
                              <Badge variant={policy.status === 'active' || policy.status === 'approved' ? 'default' : 'secondary'}>
                                {policy.status || 'draft'}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>

              {/* Comments Section */}
              <Card>
                <CardHeader 
                  className="pb-2 cursor-pointer flex flex-row items-center justify-between"
                  onClick={() => toggleSection('comments')}
                >
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Comments & Clarifications ({controlDetail.comments?.length || 0})
                  </CardTitle>
                  {expandedSections.comments ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </CardHeader>
                {expandedSections.comments && (
                  <CardContent>
                    {controlDetail.comments?.length === 0 ? (
                      <p className="text-sm text-gray-500">No comments yet</p>
                    ) : (
                      <div className="space-y-3">
                        {controlDetail.comments?.map(comment => (
                          <div 
                            key={comment.id} 
                            className={`p-3 rounded border-l-4 ${
                              comment.comment_type === 'clarification_request' 
                                ? 'bg-orange-50 border-l-orange-500' 
                                : comment.comment_type === 'clarification_response'
                                ? 'bg-blue-50 border-l-blue-500'
                                : 'bg-gray-50 border-l-gray-300'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-gray-500" />
                                <span className="text-sm font-medium">{comment.author_name || comment.author_email}</span>
                                <Badge variant="outline" className="text-xs">
                                  {COMMENT_TYPES.find(t => t.value === comment.comment_type)?.label || comment.comment_type}
                                </Badge>
                              </div>
                              <span className="text-xs text-gray-500">{formatDate(comment.created_at)}</span>
                            </div>
                            <p className="text-sm text-gray-700">{comment.content}</p>
                            {comment.comment_type === 'clarification_request' && !comment.is_resolved && (
                              <Badge variant="destructive" className="mt-2 text-xs">Pending Response</Badge>
                            )}
                            {comment.is_resolved && (
                              <Badge variant="outline" className="mt-2 text-xs text-green-600">Resolved</Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>

              {/* Review Notes */}
              {controlDetail.review.notes && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Review Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-700">{controlDetail.review.notes}</p>
                    {controlDetail.review.reviewed_by && (
                      <p className="text-xs text-gray-500 mt-2">
                        Reviewed by {controlDetail.review.reviewed_by} on {formatDate(controlDetail.review.reviewed_at)}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </>
        ) : null}
      </div>

      {/* Comment Dialog */}
      <Dialog open={showCommentDialog} onOpenChange={setShowCommentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Comment</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Comment Type</Label>
              <Select 
                value={commentData.comment_type} 
                onValueChange={(v) => setCommentData({...commentData, comment_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMMENT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Comment</Label>
              <Textarea
                value={commentData.content}
                onChange={(e) => setCommentData({...commentData, content: e.target.value})}
                placeholder="Enter your comment or clarification request..."
                rows={4}
              />
            </div>
            
            {commentData.comment_type === 'clarification_request' && (
              <p className="text-sm text-orange-600 bg-orange-50 p-2 rounded">
                This will mark the control as "Needs Clarification" and notify the control owner.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCommentDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddComment}>
              <Send className="h-4 w-4 mr-2" />
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Evidence / Document Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {previewPayload?.source === 'document' ? <FileText className="h-4 w-4" /> : <ImageIcon className="h-4 w-4" />}
              {previewPayload?.title || 'Preview'}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-auto flex-1 min-h-[300px] bg-gray-50 rounded border">
            {previewLoading || previewPayload?.kind === 'loading' ? (
              <div className="flex items-center justify-center py-16 text-sm text-gray-500">Loading preview…</div>
            ) : previewPayload?.kind === 'error' ? (
              <div className="p-6 text-sm text-red-600">{previewPayload.error}</div>
            ) : previewPayload?.kind === 'empty' ? (
              <div className="p-6 text-sm text-gray-500">No file attached to preview.</div>
            ) : previewPayload?.kind === 'snapshot' ? (
              <pre className="text-xs p-4 whitespace-pre-wrap break-words">{JSON.stringify(previewPayload.snapshot, null, 2)}</pre>
            ) : previewPayload?.kind === 'file' ? (
              (() => {
                const t = (previewPayload.file_type || '').toLowerCase();
                const src = `${API_BASE}${previewPayload.inline_url}`;
                if (['png','jpg','jpeg','gif','webp','svg'].includes(t)) {
                  return <img src={src} alt={previewPayload.title} className="w-full h-auto" data-testid="preview-image" />;
                }
                if (t === 'pdf') {
                  return <iframe title={previewPayload.title} src={src} className="w-full h-[70vh]" data-testid="preview-pdf" />;
                }
                if (['txt','md','json','csv','html'].includes(t)) {
                  return <iframe title={previewPayload.title} src={src} className="w-full h-[70vh] bg-white" data-testid="preview-text" />;
                }
                return (
                  <div className="p-6 text-sm">
                    <p className="text-gray-700">Preview not supported for <code className="text-xs bg-gray-100 px-1 rounded">{t || 'this file type'}</code>.</p>
                    <a
                      href={src}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 mt-3 px-3 py-1.5 border rounded-md text-xs hover:bg-white"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      Open in new tab
                    </a>
                  </div>
                );
              })()
            ) : (
              <div className="p-6 text-sm text-gray-500">Nothing to preview.</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Policy Preview Modal */}
      <Dialog open={!!policyPreview} onOpenChange={(o) => { if (!o) setPolicyPreview(null); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="policy-preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-indigo-600" />
              {policyPreview?.title || 'Policy'}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-auto flex-1 min-h-[300px] bg-white rounded border p-4">
            {policyPreview?.description && (
              <p className="text-sm text-gray-600 mb-3 italic">{policyPreview.description}</p>
            )}
            <div className="text-xs text-gray-500 flex flex-wrap gap-3 mb-4 border-b pb-2">
              <span>Version <strong>v{policyPreview?.version || 1}</strong></span>
              <span>Owner: {policyPreview?.owner || 'Unassigned'}</span>
              <span>Category: {policyPreview?.category || '—'}</span>
              <span>Status: <Badge variant={policyPreview?.status === 'active' || policyPreview?.status === 'approved' ? 'default' : 'secondary'} className="text-[10px]">{policyPreview?.status || 'draft'}</Badge></span>
            </div>
            {policyPreview?.content ? (
              <div className="prose prose-sm max-w-none whitespace-pre-wrap font-sans" data-testid="policy-preview-content">
                {policyPreview.content}
              </div>
            ) : (
              <p className="text-sm text-gray-500">This policy has no content yet.</p>
            )}
          </div>
          <DialogFooter>
            {policyPreview?.id && (
              <a
                href={`${API_BASE}/api/grc/policies/${policyPreview.id}/export/pdf`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center h-9 px-3 rounded-md border text-sm hover:bg-gray-50"
              >
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </a>
            )}
            <Button variant="outline" onClick={() => setPolicyPreview(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditorReviewPage;
